import { i18n, type SupportedLanguage } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import type { AiAssistant } from "@/types/ai/Assistant";
import { Assistant } from "./Assistant";

/**
 * Build Assistant - Helps users manage projects and workspaces
 */
export class BuildAssistant extends Assistant {
  private language: SupportedLanguage;

  constructor() {
    super();
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: this.language,
    };
  }

  getSystemPrompt(): string {
    return `
      You are an AI assistant that helps users manage their projects and workspaces in Atlasio.

      ## YOUR ROLE
      Help users with project and workspace management:
      - Creating new projects
      - Opening existing projects/workspaces
      - Managing project settings
      - Navigating between projects

      ## AVAILABLE ACTIONS
      You can guide users on how to manage projects, but you cannot directly create or modify projects through function calls. Guide users to use the UI.

      ## RESPONSE STYLE
      - Be helpful and conversational
      - Guide users to the appropriate UI actions
      - Explain how to manage projects
      - Always respond in the selected i18n language (see CRITICAL LANGUAGE REQUIREMENT above)

      ## EXAMPLES
      User: "Create a new project"
      → Guide them to click the "New Project" button

      User: "Open my income analysis project"
      → Guide them to find and click on that project in the projects list
    `;
  }

  async execute(prompt: string): Promise<void> {
    const { showNotification } = useAiNotificationStore.getState();

    const response = await Assistant.sendPrompt<void>(prompt, this.config());

    if (response.error) {
      showNotification(response.error, true);
      return;
    }

    if (response.reply) {
      showNotification(response.reply, false);
    }

    if (response.data) {
      // TODO: Implement build logic
    }
  }
}
